// File: src/services/activityLog.service.ts
import api from './api';

export type LogStatus = 'success' | 'error' | 'warning' | 'info';
export type LogAction = 'debug' | 'api_call' | 'api_error' | 'state_change' | 'user_action' | 'system_event';

interface LogEntry {
  action: string;
  description: string;
  status?: LogStatus;
  metadata?: any;
}

class ActivityLogService {
  private queue: LogEntry[] = [];
  private isProcessing = false;

  /**
   * Log an activity to the database
   */
  async log(entry: LogEntry): Promise<void> {
    // Add to queue
    this.queue.push({
      ...entry,
      status: entry.status || 'info',
    });

    // Process queue if not already processing
    if (!this.isProcessing) {
      // Use longer delay to ensure UI isn't blocked
      setTimeout(() => this.processQueue(), 100);
    }
  }

  /**
   * Log a debug message
   */
  async debug(action: string, description: string, metadata?: any): Promise<void> {
    try {
      await this.log({
        action: `debug_${action}`,
        description,
        status: 'info',
        metadata,
      });
    } catch (err) {
      console.error('ActivityLog debug error:', err);
    }
  }

  /**
   * Log an error
   */
  async error(action: string, description: string, error?: any): Promise<void> {
    try {
      await this.log({
        action: `error_${action}`,
        description,
        status: 'error',
        metadata: error ? { error: error.message || error.toString(), stack: error.stack } : undefined,
      });
    } catch (err) {
      console.error('ActivityLog error:', err);
    }
  }

  /**
   * Log a warning
   */
  async warning(action: string, description: string, metadata?: any): Promise<void> {
    try {
      await this.log({
        action: `warning_${action}`,
        description,
        status: 'warning',
        metadata,
      });
    } catch (err) {
      console.error('ActivityLog warning error:', err);
    }
  }

  /**
   * Log a successful action
   */
  async success(action: string, description: string, metadata?: any): Promise<void> {
    try {
      await this.log({
        action,
        description,
        status: 'success',
        metadata,
      });
    } catch (err) {
      console.error('ActivityLog success error:', err);
    }
  }

  /**
   * Process the queue of log entries
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;

    try {
      // Process entries in batches
      while (this.queue.length > 0) {
        const batch = this.queue.splice(0, 10); // Process up to 10 entries at a time
        
        // Send batch to API - only if we have an auth token
        try {
          const token = localStorage.getItem('access_token');
          if (token) {
            await api.post('/endpoints/activity_logs/batch', { logs: batch });
          } else {
            // No token, just log to console
            console.log('Activity logs (no auth):', batch);
          }
        } catch (error) {
          // If logging fails, don't retry - just console.error as fallback
          console.error('Failed to send logs to server:', error, batch);
        }
      }
    } finally {
      this.isProcessing = false;
    }
  }
}

// Export singleton instance
export const activityLog = new ActivityLogService();

// Also export for convenience
export default activityLog;